/**
 * useWorkspaceNotifications - mirrors the iOS app's notifications screen
 * data fetch exactly. Pulls from two Supabase tables:
 *
 *   1. processed_emails  → forwarded emails analysed by the email-inbound
 *      Supabase Edge Function. Type = 'email_received'.
 *   2. user_activity_log → other notification-worthy events (milestones
 *      ready, completions, failures, alerts, system events). The iOS
 *      filter excludes 'email_received' from this query so we don't get
 *      duplicates with the processed_emails feed.
 *
 * Merges + sorts by time desc, exposes a setter for optimistic state
 * updates (mark-as-read), and a refetch helper.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getSupabase } from '../lib/supabaseClient';

const activityToType = (activityType) => {
  switch (activityType) {
    case 'milestone': return 'milestone';
    case 'expense': return 'update';
    case 'generate': return 'tip';
    case 'save':
    case 'favourite': return 'update';
    case 'upload': return 'system';
    case 'email_received': return 'email_received';
    case 'email_rejected': return 'alert';
    default: return 'system';
  }
};

export const useWorkspaceNotifications = ({ limit = 50 } = {}) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Stable per-instance suffix so the Dashboard + Popover hooks each
  // open their own realtime channel (otherwise Supabase rejects the
  // second `.subscribe()` call with the same channel name).
  const channelSuffix = useRef(Math.random().toString(36).slice(2, 10));

  const fetchAll = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const supabase = getSupabase();

      // 1) Forwarded emails (processed by the email-inbound edge fn)
      const { data: emails } = await supabase
        .from('processed_emails')
        .select('*')
        .eq('user_id', user.id)
        .order('received_at', { ascending: false })
        .limit(limit);

      const emailNotifs = (emails || []).map((email) => ({
        id: email.id,
        type: 'email_received',
        title: email.from_name || email.from_email || 'Forwarded email',
        message: email.subject || '',
        time: email.received_at,
        read: !!email.reviewed,
        emailData: email,
      }));

      // 2) Other notification-worthy activities
      const { data: activities } = await supabase
        .from('user_activity_log')
        .select('*')
        .eq('user_id', user.id)
        .neq('activity_type', 'email_received')
        .order('created_at', { ascending: false })
        .limit(20);

      const activityNotifs = (activities || []).map((a) => ({
        id: a.id?.toString() || `act-${Math.random()}`,
        type: activityToType(a.activity_type),
        title: a.activity_title || 'Activity',
        message: a.activity_description || '',
        time: a.created_at || new Date().toISOString(),
        read: true,
      }));

      const merged = [...emailNotifs, ...activityNotifs].sort(
        (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()
      );
      setItems(merged);
    } catch (e) {
      console.error('[useWorkspaceNotifications] fetch error', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, limit]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await fetchAll();
      if (cancelled) return;
    })();
    return () => { cancelled = true; };
  }, [fetchAll]);

  // Realtime: when a new processed_email lands for this user, prepend it
  useEffect(() => {
    if (!user?.id) return undefined;
    const supabase = getSupabase();
    const channel = supabase
      .channel(`processed_emails_${user.id}_${channelSuffix.current}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'processed_emails', filter: `user_id=eq.${user.id}` },
        (payload) => {
          const e = payload.new;
          setItems((prev) => [
            {
              id: e.id,
              type: 'email_received',
              title: e.from_name || e.from_email || 'Forwarded email',
              message: e.subject || '',
              time: e.received_at,
              read: !!e.reviewed,
              emailData: e,
            },
            ...prev.filter((x) => x.id !== e.id),
          ]);
        },
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id]);

  const markEmailRead = useCallback(async (emailId) => {
    setItems((prev) => prev.map((n) => (n.id === emailId ? { ...n, read: true } : n)));
    try {
      const supabase = getSupabase();
      await supabase.from('processed_emails').update({ reviewed: true }).eq('id', emailId);
    } catch (e) { /* ignore */ }
  }, []);

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    if (!user?.id) return;
    try {
      const supabase = getSupabase();
      await supabase
        .from('processed_emails')
        .update({ reviewed: true })
        .eq('user_id', user.id)
        .eq('reviewed', false);
    } catch (e) { /* ignore */ }
  }, [user?.id]);

  return { items, loading, refetch: fetchAll, markEmailRead, markAllRead };
};
